import { useState, useMemo } from 'react';
import useRequest from '../../../@shared/Requests/useRequest';
import useProgressRequest from '../../../@shared/Requests/useProgressRequest';
import reportsEndpoints from '../reports.endpoints';
import ClientsEndpoint from '../../client/clients.endpoints';

const useReportsGallery = () => {
  const [selectedClientId, setSelectedClientId] = useState<number | undefined>();

  const fetchSubsidiariesRequest = useRequest<any>(ClientsEndpoint.listSubsidiaries());

  const clients = useMemo(() => {
    const subsidiaries = fetchSubsidiariesRequest.successData || [];
    const allClients: { clientId: number; name: string }[] = [];

    subsidiaries.forEach((subsidiary: any) => {
      subsidiary.clientReferences?.forEach((client: any) => {
        if (!allClients.find((c) => c.clientId === client.clientId)) {
          allClients.push(client);
        }
      });
    });

    return allClients.sort((a, b) => a.name.localeCompare(b.name));
  }, [fetchSubsidiariesRequest.successData]);

  const browseBoxesConfig = useMemo(() => reportsEndpoints.browseBoxesCsv(), []);
  const downloadCsvRequest = useProgressRequest(browseBoxesConfig);
  const downloadClientCsvRequest = useProgressRequest(browseBoxesConfig);

  fetchSubsidiariesRequest.useFetchBy(true, 'init');

  const getFormattedFileName = (scope: string) => {
    const context = 'caixas';
    const date = new Date().toISOString().split('T')[0];
    const sanitizedScope = scope
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    return `relatorio-${context}-${sanitizedScope}-${date}.csv`;
  };

  const downloadBlob = (data: any, fileName: string) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  downloadCsvRequest.useSuccess(() => {
    if (downloadCsvRequest.successData) {
      downloadBlob(downloadCsvRequest.successData, getFormattedFileName('geral'));
      downloadCsvRequest.reset();
    }
  });

  downloadClientCsvRequest.useSuccess(() => {
    if (downloadClientCsvRequest.successData) {
      const client = clients.find((c) => c.clientId === selectedClientId);
      const scope = client ? client.name : selectedClientId?.toString() || 'cliente-desconhecido';
      const fileName = getFormattedFileName(scope);
      downloadBlob(downloadClientCsvRequest.successData, fileName);
      downloadClientCsvRequest.reset();
    }
  });

  const handleDownloadCsv = () => {
    downloadCsvRequest.fetch();
  };

  const handleDownloadClientCsv = () => {
    if (!selectedClientId) return;
    downloadClientCsvRequest.fetch(reportsEndpoints.browseBoxesCsv(selectedClientId));
  };

  const error =
    downloadCsvRequest.backEndErrors?.message || downloadClientCsvRequest.backEndErrors?.message;

  return {
    selectedClientId,
    setSelectedClientId,
    clients,
    downloadCsvRequest,
    downloadClientCsvRequest,
    fetchSubsidiariesRequest,
    handleDownloadCsv,
    handleDownloadClientCsv,
    error,
  };
};

export default useReportsGallery;
